import { useEffect, useState } from 'react'
import Swal from 'sweetalert2'

import { IsGranted, ROLE, useAuth } from '../modules/auth'
import { KTIcon } from '../../_metronic/helpers'

const OVERDRAFT_PATTERN = /^\d+(\.\d{1,4})?$/

export interface IBalancePolicy {
  enforceBalance: boolean
  overdraftLimit: string
}

export interface BalancePolicyProps {
  /** Читает настройку владельца. */
  load: () => Promise<IBalancePolicy | null>
  /** Пишет обе величины разом — половинчатого состояния у настройки нет. */
  save: (policy: IBalancePolicy) => Promise<IBalancePolicy | null>
  /** Текст подтверждения на включении: последствие у владельцев разное. */
  confirmText: string
  /** Уникальный id чекбокса: на странице может быть больше одного блока. */
  inputId: string
}

/**
 * Учёт баланса владельца — мерчанта, кассы и всего, что заведут дальше.
 *
 * Один блок на всех: настройка одна и та же, отличается только то, что происходит
 * при отказе. Лимит показывается ТОЛЬКО при включённом учёте — с выключенным он не
 * работает нигде, ни гейтом, ни порогом сигнала, и показывать неработающее число
 * значит обещать поведение, которого нет.
 */
export const BalancePolicy = ({ load, save, confirmText, inputId }: BalancePolicyProps) => {
  const { currentUser } = useAuth()
  const canEdit = IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER], currentUser)

  const [policy, setPolicy] = useState<IBalancePolicy | null>(null)
  const [overdraft, setOverdraft] = useState('0')
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    // Ручка открыта ровно тем же ролям, что canEdit. Без этой проверки роль без
    // доступа получала бы 403 и видела «Ledger недоступен» вместо ничего.
    if (!canEdit) {
      setLoading(false)
      return
    }

    let active = true
    load()
      .then((loaded) => {
        if (!active) return
        if (loaded) {
          setPolicy(loaded)
          setOverdraft(loaded.overdraftLimit)
        } else {
          setFailed(true)
        }
      })
      .catch(() => active && setFailed(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit])

  const persist = async (next: IBalancePolicy) => {
    setSaving(true)
    try {
      const stored = await save(next)
      if (!stored) {
        throw new Error('save_failed')
      }
      setPolicy(stored)
      setOverdraft(stored.overdraftLimit)
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: 'Учёт баланса обновлён',
        showConfirmButton: false,
        timer: 2500,
      })
    } catch {
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'error',
        title: 'Не удалось сохранить настройку',
        showConfirmButton: false,
        timer: 4000,
      })
    } finally {
      setSaving(false)
    }
  }

  const overdraftDirty = policy !== null && overdraft !== policy.overdraftLimit
  const overdraftValid = OVERDRAFT_PATTERN.test(overdraft.trim())

  const toggle = async () => {
    if (!policy) return
    const enforceBalance = !policy.enforceBalance

    // Введённый, но не применённый лимит уходит вместе с тумблером: диалог как раз
    // просит сверить остаток, и молча выбросить набранное значит потерять действие.
    const overdraftLimit = overdraftValid ? overdraft.trim() : policy.overdraftLimit

    if (enforceBalance) {
      const { isConfirmed } = await Swal.fire({
        title: 'Учитывать баланс?',
        text: confirmText,
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Отмена',
        confirmButtonText: 'Включить',
      })
      if (!isConfirmed) return
    }

    await persist({ enforceBalance, overdraftLimit })
  }

  if (!canEdit) return null
  if (loading) return <span className='text-muted'>Загрузка…</span>
  if (failed || !policy) return <span className='text-danger'>Ledger недоступен</span>

  return (
    <div className='d-flex flex-column gap-2' style={{ maxWidth: 420 }}>
      <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
        <input
          className='form-check-input'
          type='checkbox'
          id={inputId}
          checked={policy.enforceBalance}
          disabled={saving}
          onChange={toggle}
        />
        <label className='form-check-label' htmlFor={inputId}>
          Учитывать баланс
        </label>
      </div>

      {policy.enforceBalance && (
        <div className='d-flex align-items-center gap-2'>
          <input
            type='text'
            className={`form-control form-control-sm ${overdraftDirty && !overdraftValid ? 'is-invalid' : ''}`}
            style={{ maxWidth: 160 }}
            value={overdraft}
            placeholder='0.0000'
            disabled={saving}
            onChange={(e) => setOverdraft(e.target.value)}
          />
          <span className='text-muted fs-7'>овердрафт</span>
          {overdraftDirty && (
            <>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light-success'
                title='Применить'
                disabled={saving || !overdraftValid}
                onClick={() => persist({ enforceBalance: true, overdraftLimit: overdraft.trim() })}
              >
                <KTIcon iconName='check' className='fs-2' />
              </button>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light-danger'
                title='Сбросить'
                disabled={saving}
                onClick={() => setOverdraft(policy.overdraftLimit)}
              >
                <KTIcon iconName='cross' className='fs-2' />
              </button>
            </>
          )}
        </div>
      )}

      {policy.enforceBalance && overdraftDirty && !overdraftValid && (
        <div className='text-danger fs-8'>Неотрицательное число, до 4 знаков после точки</div>
      )}
    </div>
  )
}
